import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispute } from '../../context/DisputeContext';
import { initDispute } from '../../utils/api';

const StartPage = () => {
  const navigate = useNavigate();
  const { setDispute, setLoading, setError } = useDispute();

  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email || !name) {
      setError('Please provide both your name and email');
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const data = await initDispute({
        email: email,
        name: name,
      });

      // Map backend response to expected format
      const dispute = {
        id: data.disputeId,
        partyAId: data.partyAId,
        status: data.status,
        ...data
      };

      setDispute(dispute);
      navigate('/classify');
    } catch (err) {
      setError(err.message || 'Failed to initialize dispute. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-200px)] flex items-center">
      <div className="max-w-6xl mx-auto px-4 py-12 w-full">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-5xl font-bold mb-6 bg-gradient-to-r from-primary-500 to-secondary-500 bg-clip-text text-transparent">
            Resolve Your Dispute in 72 Hours
          </h1>
          <p className="text-xl text-gray-700 mb-8 max-w-3xl mx-auto">
            TensionBridge ADR helps you settle disputes up to €15,000 without going to court.
            Fast, fair, and attorney-supervised resolution.
          </p>

          {/* Value Propositions */}
          <div className="grid md:grid-cols-3 gap-6 mb-12 max-w-4xl mx-auto">
            <div className="card text-center">
              <div className="text-4xl mb-3">⚡</div>
              <h3 className="font-bold text-lg mb-2">72-Hour Resolution</h3>
              <p className="text-sm text-gray-600">Quick resolution without lengthy court processes</p>
            </div>
            <div className="card text-center">
              <div className="text-4xl mb-3">🎯</div>
              <h3 className="font-bold text-lg mb-2">AI-Powered Matching</h3>
              <p className="text-sm text-gray-600">Smart pattern matching for fair outcomes</p>
            </div>
            <div className="card text-center">
              <div className="text-4xl mb-3">⚖️</div>
              <h3 className="font-bold text-lg mb-2">Attorney Reviewed</h3>
              <p className="text-sm text-gray-600">Every case supervised by licensed attorneys</p>
            </div>
          </div>
        </div>

        {/* Start Form */}
        <div className="max-w-md mx-auto">
          <div className="card">
            <h2 className="text-2xl font-bold mb-6 text-center">Get Started</h2>

            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                  Your Full Name
                </label>
                <input
                  id="name"
                  type="text"
                  className="input"
                  placeholder="John Doe"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  disabled={submitting}
                />
              </div>

              <div className="mb-6">
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  Your Email Address
                </label>
                <input
                  id="email"
                  type="email"
                  className="input"
                  placeholder="john@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={submitting}
                />
                <p className="text-xs text-gray-500 mt-2">
                  We'll use this to keep you updated on your dispute progress
                </p>
              </div>

              <button
                type="submit"
                className="btn-primary w-full"
                disabled={submitting}
              >
                {submitting ? 'Starting...' : 'Start Your Dispute Resolution'}
              </button>
            </form>

            {/* Trust Indicators */}
            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-xs text-gray-600 text-center">
                🔒 Confidential • 🛡️ Private • ⚖️ Attorney-Supervised
              </p>
            </div>
          </div>

          {/* How It Works */}
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-600 mb-4 font-medium">How it works:</p>
            <ol className="text-sm text-gray-700 space-y-2 text-left max-w-sm mx-auto">
              <li className="flex items-start">
                <span className="font-bold mr-2 text-primary-500">1.</span>
                <span>Describe your dispute and what you're seeking</span>
              </li>
              <li className="flex items-start">
                <span className="font-bold mr-2 text-primary-500">2.</span>
                <span>We invite the other party to respond</span>
              </li>
              <li className="flex items-start">
                <span className="font-bold mr-2 text-primary-500">3.</span>
                <span>Our AI finds fair settlement options</span>
              </li>
              <li className="flex items-start">
                <span className="font-bold mr-2 text-primary-500">4.</span>
                <span>Attorney reviews and finalizes agreement</span>
              </li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StartPage;
